/**
 * Five — sound palette.
 *
 * Aesthetic: wooden printer's-letterpress tiles being set, then read aloud.
 * The reveal sounds have a soft pitched body (an actual sung note, not a
 * blip) — pitch rises slightly across the row so the row-reveal feels like
 * a small melody. PLACED is the warmest/lowest, ABSENT is the softest/highest.
 *
 * The "win" payoff is a 3-note arpeggio on a major triad. The "loss" is a
 * gentle descending pair on a minor third — sad but not punitive.
 *
 * Every public play* goes through `audio.playIfNotMuted` so the global mute
 * toggle is respected automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type FiveSoundName =
  | 'keyTap'
  | 'keyErase'
  | 'submit'
  | 'invalid'
  | 'revealPlaced'
  | 'revealPresent'
  | 'revealAbsent'
  | 'win'
  | 'loss'
  | 'hint'
  | 'whoosh';

export const soundHaptic: Partial<Record<FiveSoundName, HapticStyle>> = {
  keyTap: 'light',
  keyErase: 'light',
  submit: 'medium',
  invalid: 'warning',
  revealPlaced: 'success',
  revealPresent: 'light',
  revealAbsent: 'light',
  win: 'success',
  loss: 'light',
  hint: 'light',
  whoosh: 'light',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Tiny "wood click" — a fast filtered noise transient with a slight pitched
 * body. Used for key taps and tile flips.
 */
function synthWoodClick(opts: {
  pitch: number;
  duration?: number;
  gain?: number;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = opts.duration ?? 0.08;
  const peak = opts.gain ?? 0.22;

  // Pitched body — short triangle pulse with a quick downward sweep.
  const osc = audio.makeOscillator(ctx, { freq: opts.pitch, type: 'triangle' });
  osc.frequency.exponentialRampToValueAtTime(opts.pitch * 0.55, t + dur);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.01,
    sustain: 0.4,
    release: dur,
    peak,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.02);

  // Click transient — white noise band-passed near the pitch.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 22 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = opts.pitch * 3;
  bp.Q.value = 1.5;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.001,
    release: 0.03,
    peak: peak * 0.6,
  });
  audio.connectThrough(src, bp, ng, master);
  src.start(t);
  src.stop(t + 0.04);
}

/** Soft sung tone with a felt body. Used for the tile-reveal notes. */
function synthTone(opts: {
  freq: number;
  duration: number;
  peak?: number;
  /** Wave shape mix: 'warm' (sine + triangle) for placed, 'soft' (sine) for absent. */
  shape?: 'warm' | 'bright' | 'soft';
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const peak = opts.peak ?? 0.18;
  const dur = opts.duration;

  const shape = opts.shape ?? 'warm';

  const osc1 = audio.makeOscillator(ctx, {
    freq: opts.freq,
    type: shape === 'soft' ? 'sine' : 'triangle',
  });

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = shape === 'bright' ? 3800 : 1900;
  lp.Q.value = 0.4;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.01,
    decay: 0.08,
    sustain: 0.5,
    release: dur,
    peak,
  });
  audio.connectThrough(osc1, lp, g, master);
  osc1.start(t);
  osc1.stop(t + dur + 0.05);

  // Subtle 5th overtone for the warm shape — gives a chime-y body without
  // becoming a bell.
  if (shape === 'warm') {
    const osc2 = audio.makeOscillator(ctx, {
      freq: opts.freq * 1.5, // perfect fifth
      type: 'sine',
    });
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.exponentialRampToValueAtTime(peak * 0.35, t + 0.02);
    g2.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    audio.connectThrough(osc2, g2, master);
    osc2.start(t);
    osc2.stop(t + dur + 0.05);
  }
}

/** A short ascending arpeggio. Used for win. */
function synthArpeggio(freqs: number[], stepMs: number, peak = 0.18): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  freqs.forEach((f, i) => {
    const t = t0 + (i * stepMs) / 1000;
    const osc = audio.makeOscillator(ctx, { freq: f, type: 'triangle' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400;
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 0.75);
    // Octave shimmer
    if (i === freqs.length - 1) {
      const osc2 = audio.makeOscillator(ctx, { freq: f * 2, type: 'sine' });
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.0001, t);
      g2.gain.exponentialRampToValueAtTime(peak * 0.5, t + 0.04);
      g2.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      audio.connectThrough(osc2, g2, master);
      osc2.start(t);
      osc2.stop(t + 1.15);
    }
  });
}

// ---------------------------------------------------------------------------
// Synthesis — concrete sounds
// ---------------------------------------------------------------------------

function synthKeyTap(): void {
  synthWoodClick({ pitch: 520, duration: 0.07, gain: 0.18 });
}

function synthKeyErase(): void {
  // Lower-pitched click — feels like undoing.
  synthWoodClick({ pitch: 280, duration: 0.09, gain: 0.18 });
}

function synthSubmit(): void {
  // Slightly more substantial click — the row "settles." Two close clicks.
  synthWoodClick({ pitch: 360, duration: 0.1, gain: 0.22 });
  const ctx = audio.getAudioContext();
  if (!ctx) return;
  window.setTimeout(() => {
    synthWoodClick({ pitch: 320, duration: 0.09, gain: 0.18 });
  }, 35);
}

function synthInvalid(): void {
  // Soft shake — two close pitches descending. Not buzzy, not punitive.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.18;
  const osc = audio.makeOscillator(ctx, { freq: 280, type: 'sine' });
  osc.frequency.linearRampToValueAtTime(230, t + dur);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.005,
    decay: 0.03,
    sustain: 0.5,
    release: dur,
    peak: 0.16,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.04);
}

// Note frequencies (Hz) — pentatonic feel. Used for tile reveals.
// Across a row of 5 tiles we'll step pitch up by ~10% per tile for placed
// reveals so the row sings up; absent reveals stay low.
const PLACED_BASE = 392.0;   // G4
const PRESENT_BASE = 349.23; // F4
const ABSENT_BASE = 261.63;  // C4

function synthRevealPlaced(positionIndex = 0): void {
  // Position 0..4 → bump pitch up a step per index for an ascending arpeggio
  // when the entire row is correct.
  const steps = [1.0, 1.122, 1.26, 1.414, 1.498];
  const f = PLACED_BASE * (steps[positionIndex] ?? 1);
  synthTone({ freq: f, duration: 0.4, peak: 0.16, shape: 'warm' });
  synthWoodClick({ pitch: f, duration: 0.05, gain: 0.08 });
}

function synthRevealPresent(positionIndex = 0): void {
  const steps = [1.0, 1.06, 1.12, 1.19, 1.26];
  const f = PRESENT_BASE * (steps[positionIndex] ?? 1);
  synthTone({ freq: f, duration: 0.32, peak: 0.13, shape: 'warm' });
  synthWoodClick({ pitch: f * 0.7, duration: 0.05, gain: 0.06 });
}

function synthRevealAbsent(positionIndex = 0): void {
  const steps = [1.0, 1.03, 1.05, 1.07, 1.1];
  const f = ABSENT_BASE * (steps[positionIndex] ?? 1);
  synthTone({ freq: f, duration: 0.22, peak: 0.09, shape: 'soft' });
  synthWoodClick({ pitch: f, duration: 0.04, gain: 0.05 });
}

function synthWin(): void {
  // C major arpeggio C-E-G with an octave shimmer on top.
  // 523 Hz (C5), 659 Hz (E5), 784 Hz (G5)
  synthArpeggio([523.25, 659.26, 783.99], 130, 0.22);
}

function synthLoss(): void {
  // Soft descending pair — F4 to D4. The "next time" note.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const notes = [349.23, 293.66];
  notes.forEach((freq, i) => {
    const ti = t + i * 0.32;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ti);
    g.gain.exponentialRampToValueAtTime(0.16, ti + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, ti + 0.9);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1500;
    audio.connectThrough(osc, lp, g, master);
    osc.start(ti);
    osc.stop(ti + 0.95);
  });
}

function synthHint(): void {
  // A bright "ping" — single high triangle with shimmer. Reads as "here."
  synthTone({ freq: 880, duration: 0.5, peak: 0.16, shape: 'bright' });
}

function synthWhoosh(): void {
  // Quick filtered noise — for page transitions / hub entries.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.32;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 360, shaping: 1.4 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(700, t);
  bp.frequency.exponentialRampToValueAtTime(2200, t + dur);
  bp.Q.value = 0.7;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.08,
    sustain: 0.6,
    release: dur,
    peak: 0.18,
  });
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class FiveSound {
  unlock(): void {
    audio.unlockAudio();
  }

  /** Play a named sound. `positionIndex` only matters for the reveal trio. */
  play(name: FiveSoundName, positionIndex = 0): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'keyTap':
          synthKeyTap();
          return;
        case 'keyErase':
          synthKeyErase();
          return;
        case 'submit':
          synthSubmit();
          return;
        case 'invalid':
          synthInvalid();
          return;
        case 'revealPlaced':
          synthRevealPlaced(positionIndex);
          return;
        case 'revealPresent':
          synthRevealPresent(positionIndex);
          return;
        case 'revealAbsent':
          synthRevealAbsent(positionIndex);
          return;
        case 'win':
          synthWin();
          return;
        case 'loss':
          synthLoss();
          return;
        case 'hint':
          synthHint();
          return;
        case 'whoosh':
          synthWhoosh();
          return;
      }
    });
  }
}

export const sound = new FiveSound();
